import { computed, extendObservable, observable } from 'mobx';

import { accessFilterFields, filters, initialState } from './constants';

class DeepSearchStore {
  constructor() {
    extendObservable(this, initialState);
  }

  clearFunctions = {};

  @observable deepSearchOpened = false;

  @observable isDeepSearch = false;

  @observable simpleFilterParams = {
    _type: 'simple',
    query: ''
  };

  lastSearchParameters = null;

  addSearchParameters = (
    searchParameters,
    filter,
    contentFilter,
    keys,
    additionalObj = {}
  ) => {
    const value = {};

    keys.forEach((key) => {
      value[key] = this.getFilter(filter, contentFilter)[key];
    });

    if (Object.keys(additionalObj).length) {
      Object.keys(additionalObj).forEach((key) => {
        value[key] = additionalObj[key];
      });
    }

    searchParameters.filters.push(value);
  };

  getFilter = (filter, field) => this.deepSearch[filter][field];

  isAccessSelectField(accessFilter, filtersType) {
    return (
      (accessFilter === filtersType.userType ||
        accessFilter === filtersType.userStatus ||
        accessFilter === filtersType.userJoinType) &&
      this.getFilter(filters.accessFilter, accessFilter).query
    );
  }

  isAccessSelectDateField(accessFilter, filtersType) {
    return (
      (accessFilter === filtersType.userPurchaseDate ||
        accessFilter === filtersType.userFirstAccess ||
        accessFilter === filtersType.userLastAccess) &&
      this.getFilter(filters.accessFilter, accessFilter).dateFilterType
    );
  }

  isUserPlusAccess(accessFilter, filtersType) {
    const { userPlusAccess } = filtersType;
    const { value } = this.getFilter(filters.accessFilter, accessFilter);

    return accessFilter === userPlusAccess && value;
  }

  isAccessPlusAll(accessFilter, filtersType) {
    const { accessPlusAll } = filtersType;
    const { value } = this.getFilter(filters.accessFilter, accessFilter);

    return accessFilter === accessPlusAll && value === 'WITHOUT_PLUS_ACCESS';
  }

  fillAccessFilterParameters(searchParameters, accessFilter) {
    if (this.isAccessSelectDateField(accessFilter, accessFilterFields)) {
      this.addSearchParameters(
        searchParameters,
        filters.accessFilter,
        accessFilter,
        ['_type', 'dateFilterType', 'from', 'until']
      );
    } else if (this.isAccessSelectField(accessFilter, accessFilterFields)) {
      this.addSearchParameters(
        searchParameters,
        filters.accessFilter,
        accessFilter,
        ['_type', 'query']
      );
    } else if (this.isUserPlusAccess(accessFilter, accessFilterFields)) {
      this.addSearchParameters(
        searchParameters,
        filters.accessFilter,
        accessFilter,
        ['_type', 'value']
      );
    } else if (this.isAccessPlusAll(accessFilter, accessFilterFields)) {
      this.addSearchParameters(
        searchParameters,
        filters.accessFilter,
        accessFilter,
        ['_type', 'value']
      );
    }

    return searchParameters;
  }

  fillUserFilterParameters(searchParameters, userFilter) {
    const filter = this.getFilter(filters.userFilter, userFilter);

    if (filter.query || filter.queryData) {
      const params = filter.query
        ? ['_type', 'query']
        : ['_type', 'min', 'max'];

      this.addSearchParameters(
        searchParameters,
        filters.userFilter,
        userFilter,
        params
      );
    }

    return searchParameters;
  }

  isUserProgressParameter(fields, contentFilter, maxRange) {
    const filter = this.getFilter(filters.contentFilter, contentFilter);
    const hasProgressChanged =
      filter.min !== maxRange.min || filter.max !== maxRange.max;

    return fields.userProgress === contentFilter && hasProgressChanged;
  }

  isUserClassParameter(fields, contentFilter) {
    return (
      fields.userClass === contentFilter &&
      this.getFilter(filters.contentFilter, contentFilter).ids.length
    );
  }

  isQuizProfileParameter(fields, contentFilter) {
    return (
      fields.quizProfile === contentFilter &&
      this.getFilter(filters.contentFilter, contentFilter).id
    );
  }

  isQuizScoreParameter(fields, contentFilter) {
    return (
      fields.quizScore === contentFilter &&
      this.getFilter(filters.contentFilter, contentFilter).ids.length
    );
  }

  isPageParameter(fields, contentFilter) {
    return (
      (fields.page === contentFilter ||
        fields.incompletePage === contentFilter) &&
      this.getFilter(filters.contentFilter, contentFilter).ids.length
    );
  }

  fillContentFilterParameters(searchParameters, contentFilter) {
    const fields = {
      userProgress: 'userProgress',
      userClass: 'userClass',
      quizProfile: 'quizProfile',
      quizScore: 'quizScore',
      page: 'page',
      incompletePage: 'incompletePage'
    };
    const maxRange = {
      min: 0,
      max: 100
    };

    if (this.isUserProgressParameter(fields, contentFilter, maxRange)) {
      this.addSearchParameters(
        searchParameters,
        filters.contentFilter,
        contentFilter,
        ['_type', 'min', 'max']
      );
    } else if (this.isUserClassParameter(fields, contentFilter)) {
      this.addSearchParameters(
        searchParameters,
        filters.contentFilter,
        contentFilter,
        ['_type', 'ids']
      );
    } else if (this.isQuizProfileParameter(fields, contentFilter)) {
      this.addSearchParameters(
        searchParameters,
        filters.contentFilter,
        contentFilter,
        ['_type', 'ids', 'id', 'profileId']
      );
    } else if (this.isQuizScoreParameter(fields, contentFilter)) {
      this.getFilter(filters.contentFilter, contentFilter).ids.forEach(
        (quizId) => {
          this.addSearchParameters(
            searchParameters,
            filters.contentFilter,
            contentFilter,
            ['_type', 'min', 'max'],
            { id: quizId }
          );
        }
      );
    } else if (this.isPageParameter(fields, contentFilter)) {
      this.addSearchParameters(
        searchParameters,
        filters.contentFilter,
        contentFilter,
        ['_type', 'ids', 'completed']
      );
    }

    return searchParameters;
  }

  @computed
  get errorFields() {
    const quizProfile =
      this.deepSearch && this.deepSearch.contentFilter.quizProfile;

    return quizProfile && quizProfile.id && !quizProfile.profileId;
  }

  @computed
  get searchParameters() {
    let searchParameters = {
      filters: []
    };

    this.deepSearch &&
      Object.keys(this.deepSearch.accessFilter).forEach((accessFilter) => {
        searchParameters = this.fillAccessFilterParameters(
          searchParameters,
          accessFilter
        );
      });

    this.deepSearch &&
      Object.keys(this.deepSearch.userFilter).forEach((userFilter) => {
        searchParameters = this.fillUserFilterParameters(
          searchParameters,
          userFilter
        );
      });

    this.deepSearch &&
      Object.keys(this.deepSearch.contentFilter).forEach((contentFilter) => {
        searchParameters = this.fillContentFilterParameters(
          searchParameters,
          contentFilter
        );
      });

    return searchParameters;
  }
}

export default new DeepSearchStore();
